import React, { useState, useEffect, useCallback } from "react";
import { Helmet } from "react-helmet-async";
import { useParams, useNavigate } from "react-router-dom";
import {
  Container,
  Typography,
  Box,
  Breadcrumbs,
  Card,
  CardContent,
  Tabs,
  Tab,
  Grid,
  Button,
  Chip,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  CircularProgress,
} from "@mui/material";
import {
  Edit as EditIcon,
  ArrowBack as BackIcon,
  SportsSoccer as MatchIcon,
  AccessTime as TimeIcon,
  Lock as LockIcon,
  CheckCircle as CheckIcon,
  Delete as DeleteIcon,
  OpenInFull as ExpandIcon,
  PlayArrow as PlayIcon,
  AttachMoney as BudgetIcon,
} from "@mui/icons-material";
import { format } from "date-fns";
import {
  MatchdayStatusBadge,
  EditMatchdayModal,
} from "../components/matchdays";
import WorldCupCheck from "../components/tournaments/WorldCupCheck";
import { get, del, patch } from "../utils/networkUtils";
import { Link as RouterLink } from "react-router-dom";

// Tab Panel Component
const TabPanel = ({ children, value, index, ...other }) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    id={`matchday-tabpanel-${index}`}
    aria-labelledby={`matchday-tab-${index}`}
    {...other}
  >
    {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
  </div>
);

const MatchdayDetailPage = () => {
  const { id: urlTournamentId, matchdayId } = useParams();
  const navigate = useNavigate();

  // Resolve tournament UUID (handle both UUID and short code like 'wc-2026')
  const uuidRegex =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  const tournamentId = uuidRegex.test(urlTournamentId)
    ? urlTournamentId
    : "53865fe2-91a8-439d-a406-b6eafd6c10ce";

  const [tournament, setTournament] = useState(null);
  const [matchday, setMatchday] = useState(null);
  const [assignedMatches, setAssignedMatches] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);

  // Fetch matchday data from API
  const fetchMatchdayData = useCallback(async () => {
    try {
      setLoading(true);

      // Fetch matchday details with assigned matches
      const response = await get(`/admin/matchday/${matchdayId}`);

      if (response?.data?.matchday) {
        const md = response.data.matchday;
        const matches = response.data.matches || [];

        setMatchday({
          id: md.id,
          tournamentId: md.tournamentCalendarId,
          matchdayNumber: md.matchdayNumber,
          name: md.name,
          expectedMatchCount: md.expectedMatchCount,
          assignedMatches: md.assignedMatches || 0,
          status: md.status,
          startsAt: md.startsAt,
          endsAt: md.endsAt,
          locksAt: md.locksAt,
          budgetInitial: md.budgetInitial,
          createdAt: md.createdAt,
          updatedAt: md.updatedAt,
        });

        // Map assigned matches
        const mappedMatches = matches.map((m) => ({
          id: m.id,
          optaFixtureId: m.optaFixtureId,
          kickoffAt: m.kickoffAt,
          status: m.status,
          homeScore: m.homeScore,
          awayScore: m.awayScore,
          homeContestantId: m.homeContestantId,
          awayContestantId: m.awayContestantId,
          homeContestantName: m.homeContestantName || "TBD",
          awayContestantName: m.awayContestantName || "TBD",
        }));

        setAssignedMatches(mappedMatches);

        // Set tournament info
        setTournament({
          id: tournamentId,
          name: "World Cup 2026",
        });
      } else {
        setMatchday(null);
      }
    } catch (error) {
      console.error("Failed to fetch matchday:", error);
      setSnackbar({
        open: true,
        message: "Failed to load matchday data",
        severity: "error",
      });
      setMatchday(null);
    } finally {
      setLoading(false);
    }
  }, [matchdayId, tournamentId]);

  useEffect(() => {
    fetchMatchdayData();
  }, [fetchMatchdayData]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await patch(`/admin/matchday/${matchdayId}/status`, {
        status: newStatus,
      });

      setMatchday((prev) => ({ ...prev, status: newStatus }));
      setSnackbar({
        open: true,
        message: `Matchday status changed to ${newStatus}`,
        severity: "success",
      });
    } catch (error) {
      console.error("Failed to update status:", error);
      setSnackbar({
        open: true,
        message: error?.response?.data?.message || "Failed to update status",
        severity: "error",
      });
    }
  };

  const handleEditSubmit = async (formData) => {
    try {
      const payload = {
        name: formData.name,
        expectedMatchCount: formData.expectedMatchCount,
        status: formData.status,
        budgetInitial: formData.budgetInitial,
      };

      const response = await patch(`/admin/matchday/${matchdayId}`, payload);

      if (response?.data) {
        setMatchday((prev) => ({
          ...prev,
          name: response.data.name,
          expectedMatchCount: response.data.expectedMatchCount,
          status: response.data.status,
          budgetInitial: response.data.budgetInitial,
          updatedAt: response.data.updatedAt,
        }));
        setSnackbar({
          open: true,
          message: `Matchday "${formData.name}" updated successfully`,
          severity: "success",
        });
        setEditModalOpen(false);
      }
    } catch (error) {
      console.error("Failed to update matchday:", error);
      setSnackbar({
        open: true,
        message: error?.response?.data?.message || "Failed to update matchday",
        severity: "error",
      });
    }
  };

  const handleDelete = async () => {
    if (assignedMatches.length > 0) {
      setSnackbar({
        open: true,
        message: "Cannot delete matchday with assigned matches",
        severity: "error",
      });
      setDeleteDialog(false);
      return;
    }

    try {
      await del(`/admin/matchday/${matchdayId}`);

      setSnackbar({
        open: true,
        message: "Matchday deleted successfully",
        severity: "success",
      });
      setDeleteDialog(false);
      setTimeout(() => {
        navigate(`/dashboard/tournaments/${urlTournamentId}/matchdays`);
      }, 1500);
    } catch (error) {
      console.error("Failed to delete matchday:", error);
      setSnackbar({
        open: true,
        message: error?.response?.data?.message || "Failed to delete matchday",
        severity: "error",
      });
      setDeleteDialog(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Not set";
    try {
      return format(new Date(dateString), "MMMM dd, yyyy HH:mm");
    } catch {
      return "Invalid date";
    }
  };

  if (loading) {
    return (
      <Container maxWidth="xl">
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "400px",
          }}
        >
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (!matchday) {
    return (
      <Container maxWidth="xl">
        <Alert severity="error">Matchday not found</Alert>
      </Container>
    );
  }

  const isFull =
    matchday.expectedMatchCount &&
    assignedMatches.length >= matchday.expectedMatchCount;
  const isOverCapacity =
    matchday.expectedMatchCount &&
    assignedMatches.length > matchday.expectedMatchCount;

  return (
    <WorldCupCheck>
      <Helmet>
        <title>{matchday.name} | DreamTeam Admin</title>
      </Helmet>

      <Container maxWidth="xl">
        {/* Breadcrumbs */}
        <Breadcrumbs sx={{ mb: 2 }}>
          <Typography
            component={RouterLink}
            to="/dashboard/tournaments"
            sx={{
              color: "inherit",
              textDecoration: "none",
              cursor: "pointer",
              "&:hover": { textDecoration: "underline" },
            }}
          >
            Tournaments
          </Typography>
          <Typography
            component={RouterLink}
            to={`/dashboard/tournaments/${urlTournamentId}`}
            sx={{
              color: "inherit",
              textDecoration: "none",
              cursor: "pointer",
              "&:hover": { textDecoration: "underline" },
            }}
          >
            {tournament?.name}
          </Typography>
          <Typography
            component={RouterLink}
            to={`/dashboard/tournaments/${urlTournamentId}/matchdays`}
            sx={{
              color: "inherit",
              textDecoration: "none",
              cursor: "pointer",
              "&:hover": { textDecoration: "underline" },
            }}
          >
            Matchdays
          </Typography>
          <Typography color="text.primary">{matchday.name}</Typography>
        </Breadcrumbs>

        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            mb: 3,
          }}
        >
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1 }}>
              <Typography variant="h4" fontWeight={700}>
                {matchday.name}
              </Typography>
              <MatchdayStatusBadge status={matchday.status} />
            </Box>
            <Typography variant="subtitle1" color="text.secondary">
              {tournament?.name} • Matchday #{matchday.matchdayNumber}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<BackIcon />}
              onClick={() =>
                navigate(`/dashboard/tournaments/${urlTournamentId}/matchdays`)
              }
            >
              Back
            </Button>
            <Button
              variant="contained"
              startIcon={<EditIcon />}
              onClick={() => setEditModalOpen(true)}
            >
              Edit
            </Button>
            {matchday.status === "upcoming" && (
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={() => setDeleteDialog(true)}
              >
                Delete
              </Button>
            )}
          </Box>
        </Box>

        {/* Status Alerts */}
        {isOverCapacity && (
          <Alert severity="error" sx={{ mb: 3 }}>
            This matchday has{" "}
            {assignedMatches.length - matchday.expectedMatchCount} more
            match(es) than expected!
          </Alert>
        )}
        {isFull && !isOverCapacity && (
          <Alert severity="success" sx={{ mb: 3 }}>
            This matchday is at full capacity with {assignedMatches.length}{" "}
            matches.
          </Alert>
        )}

        {/* Tabs */}
        <Card>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            sx={{ borderBottom: 1, borderColor: "divider", px: 2, pt: 2 }}
          >
            <Tab label="Overview" />
            <Tab label={`Matches (${assignedMatches.length})`} />
            <Tab label="Assign Matches" />
          </Tabs>

          <CardContent>
            {/* Overview Tab */}
            <TabPanel value={activeTab} index={0}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" fontWeight={600} gutterBottom>
                    Matchday Details
                  </Typography>
                  <List>
                    <ListItem>
                      <ListItemIcon>
                        <MatchIcon />
                      </ListItemIcon>
                      <ListItemText
                        primary="Matchday Number"
                        secondary={matchday.matchdayNumber}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <MatchIcon />
                      </ListItemIcon>
                      <ListItemText primary="Name" secondary={matchday.name} />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <MatchIcon />
                      </ListItemIcon>
                      <ListItemText
                        primary="Expected Matches"
                        secondary={matchday.expectedMatchCount}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <MatchIcon />
                      </ListItemIcon>
                      <ListItemText
                        primary="Assigned Matches"
                        secondary={
                          <Chip
                            label={`${assignedMatches.length} / ${matchday.expectedMatchCount}`}
                            color={isFull ? "success" : "default"}
                            size="small"
                          />
                        }
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <BudgetIcon />
                      </ListItemIcon>
                      <ListItemText
                        primary="Initial Budget"
                        secondary={
                          matchday.budgetInitial != null
                            ? `£${(matchday.budgetInitial / 1_000_000).toFixed(
                                1
                              )}m`
                            : "Not set"
                        }
                      />
                    </ListItem>
                  </List>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" fontWeight={600} gutterBottom>
                    Schedule
                  </Typography>
                  <List>
                    <ListItem>
                      <ListItemIcon>
                        <TimeIcon />
                      </ListItemIcon>
                      <ListItemText
                        primary="Starts At"
                        secondary={formatDate(matchday.startsAt)}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <TimeIcon />
                      </ListItemIcon>
                      <ListItemText
                        primary="Ends At"
                        secondary={formatDate(matchday.endsAt)}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <LockIcon />
                      </ListItemIcon>
                      <ListItemText
                        primary="Locks At (Transfer Deadline)"
                        secondary={formatDate(matchday.locksAt)}
                      />
                    </ListItem>
                  </List>
                </Grid>
              </Grid>

              <Divider sx={{ my: 3 }} />

              <Typography variant="h6" fontWeight={600} gutterBottom>
                Status Actions
              </Typography>
              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                {matchday.status === "upcoming" && (
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<CheckIcon />}
                    onClick={() => handleStatusChange("active")}
                  >
                    Activate Matchday
                  </Button>
                )}
                {matchday.status === "active" && (
                  <Button
                    variant="contained"
                    color="warning"
                    startIcon={<LockIcon />}
                    onClick={() => handleStatusChange("locked")}
                  >
                    Lock Matchday
                  </Button>
                )}
                {matchday.status === "locked" && (
                  <Button
                    variant="contained"
                    color="info"
                    startIcon={<CheckIcon />}
                    onClick={() => handleStatusChange("completed")}
                  >
                    Mark as Completed
                  </Button>
                )}
                {matchday.status === "completed" && (
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<PlayIcon />}
                    onClick={() => handleStatusChange("upcoming")}
                  >
                    Reopen Matchday
                  </Button>
                )}
              </Box>
            </TabPanel>

            {/* Matches Tab */}
            <TabPanel value={activeTab} index={1}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Typography variant="h6" fontWeight={600}>
                  Assigned Matches
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={<ExpandIcon />}
                  onClick={() =>
                    navigate(
                      `/dashboard/tournaments/${urlTournamentId}/matchday-builder?matchday=${matchdayId}`
                    )
                  }
                >
                  Open in Builder
                </Button>
              </Box>

              {assignedMatches.length === 0 ? (
                <Alert severity="info">
                  No matches assigned yet. Go to the "Assign Matches" tab to add
                  matches.
                </Alert>
              ) : (
                <List>
                  {assignedMatches.map((match, index) => (
                    <React.Fragment key={match.id}>
                      <ListItem>
                        <ListItemIcon>
                          <Typography fontWeight={700} color="primary">
                            {index + 1}
                          </Typography>
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 2,
                              }}
                            >
                              <Typography fontWeight={600}>
                                {match.homeContestantName}
                              </Typography>
                              <Typography color="text.secondary">vs</Typography>
                              <Typography fontWeight={600}>
                                {match.awayContestantName}
                              </Typography>
                            </Box>
                          }
                          secondary={
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 2,
                                mt: 0.5,
                              }}
                            >
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                {formatDate(match.kickoffAt)}
                              </Typography>
                              <Chip
                                label={
                                  match.status === "fixture"
                                    ? "Upcoming"
                                    : match.status
                                }
                                size="small"
                                color={
                                  match.status === "played"
                                    ? "success"
                                    : "default"
                                }
                              />
                              {match.status === "played" && (
                                <Typography
                                  variant="body2"
                                  fontWeight={600}
                                  color="primary"
                                >
                                  {match.homeScore} - {match.awayScore}
                                </Typography>
                              )}
                            </Box>
                          }
                        />
                      </ListItem>
                      {index < assignedMatches.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              )}
            </TabPanel>

            {/* Assign Matches Tab */}
            <TabPanel value={activeTab} index={2}>
              <Box sx={{ textAlign: "center", py: 6 }}>
                <Typography variant="h6" gutterBottom>
                  Assign Matches to {matchday.name}
                </Typography>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{ mb: 3 }}
                >
                  Use the Matchday Builder to assign matches to this matchday
                </Typography>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<ExpandIcon />}
                  onClick={() =>
                    navigate(
                      `/dashboard/tournaments/${urlTournamentId}/matchday-builder?matchday=${matchdayId}`
                    )
                  }
                >
                  Open Matchday Builder
                </Button>
              </Box>
            </TabPanel>
          </CardContent>
        </Card>
      </Container>

      {/* Delete Dialog */}
      <Dialog open={deleteDialog} onClose={() => setDeleteDialog(false)}>
        <DialogTitle>Delete Matchday?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{matchday.name}"?
          </Typography>
          {assignedMatches.length > 0 && (
            <Alert severity="error" sx={{ mt: 2 }}>
              This matchday has {assignedMatches.length} assigned match(es).
              Remove all matches first.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(false)}>Cancel</Button>
          <Button
            onClick={handleDelete}
            color="error"
            variant="contained"
            disabled={assignedMatches.length > 0}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Matchday Modal */}
      <EditMatchdayModal
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        matchday={matchday}
        onSubmit={handleEditSubmit}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </WorldCupCheck>
  );
};

export default MatchdayDetailPage;
